import axios from 'axios';
import { clearAccessToken, getAccessToken } from '../auth/tokenStorage';
import { environment } from '../config/environment';
import { ApiError, createApiError, isApiError } from './errors';

export const CORE_API_UNAUTHORIZED_EVENT = 'elite-core-auth-unauthorized';

const getRequestId = (headers?: Record<string, unknown>): string | null => {
  if (!headers) return null;
  const raw = (headers['x-request-id'] || headers['request-id'] || headers['x-correlation-id']) as string | undefined;
  return raw ? String(raw) : null;
};

export const coreApi = axios.create({
  baseURL: environment.coreServerUrl,
  timeout: 15000,
});

coreApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  config.headers = config.headers ?? {};
  config.headers['X-Requested-With'] = 'CommandCenter';

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

coreApi.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const normalized = normalizeApiError(error);

    if (normalized.kind === 'authorization') {
      clearAccessToken();
      window.dispatchEvent(new CustomEvent(CORE_API_UNAUTHORIZED_EVENT));
    }

    return Promise.reject(normalized);
  }
);

export const normalizeApiError = (error: unknown): ApiError => {
  if (isApiError(error)) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status;
    const responseData = error.response?.data as
      | { error?: string; message?: string; errorMessage?: string }
      | undefined;
    const requestId = getRequestId(error.response?.headers as Record<string, unknown> | undefined);

    const message =
      responseData?.errorMessage ||
      responseData?.error ||
      responseData?.message ||
      error.message ||
      'An unexpected request error occurred.';

    if (!error.response) {
      return createApiError(
        'network',
        'Unable to reach the Elite Core server. Please check your connection and try again.',
        undefined,
        error,
        requestId
      );
    }

    if (statusCode === 401) {
      return createApiError('authorization', message, statusCode, error.response?.data, requestId);
    }

    if (statusCode === 403) {
      return createApiError('forbidden', message, statusCode, error.response?.data, requestId);
    }

    if (statusCode === 404) {
      return createApiError('not-found', message, statusCode, error.response?.data, requestId);
    }

    if (statusCode === 501) {
      return createApiError('unavailable', message, statusCode, error.response?.data, requestId);
    }

    if (statusCode === 400 || statusCode === 422) {
      return createApiError('validation', message, statusCode, error.response?.data, requestId);
    }

    return createApiError('unknown', message, statusCode, error.response?.data, requestId);
  }

  if (error instanceof Error) {
    return createApiError('unknown', error.message, undefined, error);
  }

  return createApiError('unknown', 'An unexpected request error occurred.', undefined, error);
};

export const get = async <T>(url: string, config?: Parameters<typeof coreApi.get>[1]): Promise<T> => {
  const response = await coreApi.get<T>(url, config);
  return response.data;
};

export const post = async <T>(url: string, data?: unknown, config?: Parameters<typeof coreApi.post>[2]): Promise<T> => {
  const response = await coreApi.post<T>(url, data, config);
  return response.data;
};

export const put = async <T>(url: string, data?: unknown, config?: Parameters<typeof coreApi.put>[2]): Promise<T> => {
  const response = await coreApi.put<T>(url, data, config);
  return response.data;
};

export const patch = async <T>(url: string, data?: unknown, config?: Parameters<typeof coreApi.patch>[2]): Promise<T> => {
  const response = await coreApi.patch<T>(url, data, config);
  return response.data;
};

export const remove = async <T>(url: string, config?: Parameters<typeof coreApi.delete>[1]): Promise<T> => {
  const response = await coreApi.delete<T>(url, config);
  return response.data;
};
